import React, { useState, useEffect } from "react";
import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button as UIButton } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  MapPin,
  Eye,
  Users,
  MousePointer,
  TrendingUp,
  TrendingDown,
  Plane,
  Car,
  ArrowUpRight,
  ArrowDownRight,
  RefreshCw,
  Filter,
  Download,
} from "lucide-react";

interface LandingPageMetrics {
  page_slug: string;
  page_name: string;
  page_type: "location" | "airport_transfer" | "route";
  parent_city: "Leicester" | "Birmingham" | "UK-wide";
  metrics: {
    page_views: number;
    unique_visitors: number;
    conversions: number;
    conversion_rate: number;
    bounce_rate: number;
    avg_session_duration: number;
    top_traffic_source: string;
  };
  trends: {
    page_views_change: number;
    conversions_change: number;
    bounce_rate_change: number;
  };
  last_updated: string;
}

// Mock data for demonstration
const generateMockData = (): LandingPageMetrics[] => {
  const locations = [
    {
      slug: "leicester",
      name: "Leicester",
      type: "location" as const,
      city: "Leicester" as const,
    },
    {
      slug: "birmingham",
      name: "Birmingham",
      type: "location" as const,
      city: "Birmingham" as const,
    },
    {
      slug: "loughborough",
      name: "Loughborough",
      type: "location" as const,
      city: "Leicester" as const,
    },
    {
      slug: "solihull",
      name: "Solihull",
      type: "location" as const,
      city: "Birmingham" as const,
    },
    {
      slug: "coventry",
      name: "Coventry",
      type: "location" as const,
      city: "Birmingham" as const,
    },
    {
      slug: "transfer-heathrow",
      name: "Heathrow Transfer",
      type: "airport_transfer" as const,
      city: "UK-wide" as const,
    },
    {
      slug: "transfer-birmingham",
      name: "Birmingham Airport Transfer",
      type: "airport_transfer" as const,
      city: "UK-wide" as const,
    },
    {
      slug: "leicester-to-heathrow",
      name: "Leicester to Heathrow",
      type: "route" as const,
      city: "Leicester" as const,
    },
    {
      slug: "birmingham-to-heathrow",
      name: "Birmingham to Heathrow",
      type: "route" as const,
      city: "Birmingham" as const,
    },
  ];

  return locations.map((location) => ({
    page_slug: location.slug,
    page_name: location.name,
    page_type: location.type,
    parent_city: location.city,
    metrics: {
      page_views: Math.floor(Math.random() * 5000) + 500,
      unique_visitors: Math.floor(Math.random() * 3000) + 300,
      conversions: Math.floor(Math.random() * 150) + 10,
      conversion_rate: Math.round((Math.random() * 8 + 2) * 100) / 100,
      bounce_rate: Math.round((Math.random() * 40 + 30) * 100) / 100,
      avg_session_duration: Math.floor(Math.random() * 300) + 60,
      top_traffic_source: [
        "Google Search",
        "Direct",
        "Social Media",
        "Referral",
      ][Math.floor(Math.random() * 4)],
    },
    trends: {
      page_views_change: Math.round((Math.random() * 40 - 20) * 100) / 100,
      conversions_change: Math.round((Math.random() * 30 - 15) * 100) / 100,
      bounce_rate_change: Math.round((Math.random() * 20 - 10) * 100) / 100,
    },
    last_updated: new Date().toISOString(),
  }));
};

export function LandingPageAnalyticsWidget() {
  const [metrics, setMetrics] = useState<LandingPageMetrics[]>([]);
  const [loading, setLoading] = useState(true);
  const [filterType, setFilterType] = useState<
    "all" | "location" | "airport_transfer" | "route"
  >("all");
  const [filterCity, setFilterCity] = useState<
    "all" | "Leicester" | "Birmingham" | "UK-wide"
  >("all");

  useEffect(() => {
    // Simulate data loading
    const loadData = async () => {
      setLoading(true);
      await new Promise((resolve) => setTimeout(resolve, 1000));
      setMetrics(generateMockData());
      setLoading(false);
    };
    loadData();
  }, []);

  // Filter metrics
  const filteredMetrics = metrics.filter((metric) => {
    const matchesType = filterType === "all" || metric.page_type === filterType;
    const matchesCity =
      filterCity === "all" || metric.parent_city === filterCity;
    return matchesType && matchesCity;
  });

  // Calculate totals
  const totals = filteredMetrics.reduce(
    (acc, metric) => ({
      total_page_views: acc.total_page_views + metric.metrics.page_views,
      total_conversions: acc.total_conversions + metric.metrics.conversions,
      avg_conversion_rate:
        acc.avg_conversion_rate + metric.metrics.conversion_rate,
      avg_bounce_rate: acc.avg_bounce_rate + metric.metrics.bounce_rate,
    }),
    {
      total_page_views: 0,
      total_conversions: 0,
      avg_conversion_rate: 0,
      avg_bounce_rate: 0,
    },
  );

  totals.avg_conversion_rate =
    totals.avg_conversion_rate / filteredMetrics.length || 0;
  totals.avg_bounce_rate = totals.avg_bounce_rate / filteredMetrics.length || 0;

  // Sort by performance
  const sortedByPerformance = [...filteredMetrics].sort(
    (a, b) => b.metrics.conversions - a.metrics.conversions,
  );
  const topPerformers = sortedByPerformance.slice(0, 5);
  const needsAttention = [...filteredMetrics]
    .filter((m) => m.metrics.conversion_rate < 2 || m.metrics.bounce_rate > 70)
    .slice(0, 5);

  const TrendIcon = ({ change }: { change: number }) => {
    if (change > 0) return <ArrowUpRight className="h-4 w-4 text-green-600" />;
    if (change < 0) return <ArrowDownRight className="h-4 w-4 text-red-600" />;
    return <div className="h-4 w-4" />;
  };

  const formatDuration = (seconds: number) => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}m ${remainingSeconds}s`;
  };

  if (loading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <RefreshCw className="h-5 w-5 animate-spin" />
            Loading Landing Page Analytics...
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="h-64 flex items-center justify-center">
            <div className="text-gray-500">Loading analytics data...</div>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-6 text-center">
            <Eye className="h-8 w-8 text-primary mx-auto mb-3" />
            <div className="text-2xl font-bold text-gray-900">
              {totals.total_page_views.toLocaleString()}
            </div>
            <div className="text-gray-600">Total Page Views</div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6 text-center">
            <MousePointer className="h-8 w-8 text-primary mx-auto mb-3" />
            <div className="text-2xl font-bold text-gray-900">
              {totals.total_conversions.toLocaleString()}
            </div>
            <div className="text-gray-600">Total Conversions</div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6 text-center">
            <TrendingUp className="h-8 w-8 text-primary mx-auto mb-3" />
            <div className="text-2xl font-bold text-gray-900">
              {totals.avg_conversion_rate.toFixed(2)}%
            </div>
            <div className="text-gray-600">Avg Conversion Rate</div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6 text-center">
            <Users className="h-8 w-8 text-primary mx-auto mb-3" />
            <div className="text-2xl font-bold text-gray-900">
              {totals.avg_bounce_rate.toFixed(1)}%
            </div>
            <div className="text-gray-600">Avg Bounce Rate</div>
          </CardContent>
        </Card>
      </div>

      {/* Main Analytics */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center justify-between">
            <span className="flex items-center gap-2">
              <MapPin className="h-5 w-5" />
              Landing Page Performance
            </span>
            <div className="flex gap-2">
              <UIButton variant="outline" size="sm">
                <Download className="h-4 w-4 mr-2" />
                Export
              </UIButton>
              <UIButton
                variant="outline"
                size="sm"
                onClick={() => setMetrics(generateMockData())}
              >
                <RefreshCw className="h-4 w-4 mr-2" />
                Refresh
              </UIButton>
            </div>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <Tabs defaultValue="overview">
            <TabsList className="grid w-full grid-cols-4">
              <TabsTrigger value="overview">Overview</TabsTrigger>
              <TabsTrigger value="top-performers">Top Performers</TabsTrigger>
              <TabsTrigger value="needs-attention">Needs Attention</TabsTrigger>
              <TabsTrigger value="detailed">Detailed View</TabsTrigger>
            </TabsList>

            <TabsContent value="overview" className="space-y-4">
              <div className="flex gap-4 mb-4">
                <select
                  value={filterType}
                  onChange={(e) => setFilterType(e.target.value as any)}
                  className="px-3 py-2 border rounded-md"
                >
                  <option value="all">All Page Types</option>
                  <option value="location">Location Pages</option>
                  <option value="airport_transfer">Airport Transfers</option>
                  <option value="route">Route Pages</option>
                </select>

                <select
                  value={filterCity}
                  onChange={(e) => setFilterCity(e.target.value as any)}
                  className="px-3 py-2 border rounded-md"
                >
                  <option value="all">All Regions</option>
                  <option value="Leicester">Leicester Area</option>
                  <option value="Birmingham">Birmingham Area</option>
                  <option value="UK-wide">UK-wide</option>
                </select>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {filteredMetrics.slice(0, 9).map((metric) => (
                  <Card key={metric.page_slug}>
                    <CardContent className="p-4">
                      <div className="flex items-start justify-between mb-2">
                        <div>
                          <h4 className="font-semibold">{metric.page_name}</h4>
                          <Badge variant="outline" className="text-xs">
                            {metric.page_type.replace("_", " ")}
                          </Badge>
                        </div>
                        <div className="text-right">
                          <div className="text-sm font-bold">
                            {metric.metrics.conversions}
                          </div>
                          <div className="text-xs text-gray-500">
                            conversions
                          </div>
                        </div>
                      </div>

                      <div className="space-y-1 text-sm">
                        <div className="flex justify-between">
                          <span className="text-gray-600">Page Views:</span>
                          <span className="flex items-center gap-1">
                            {metric.metrics.page_views.toLocaleString()}
                            <TrendIcon
                              change={metric.trends.page_views_change}
                            />
                          </span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-600">Conv. Rate:</span>
                          <span className="flex items-center gap-1">
                            {metric.metrics.conversion_rate}%
                            <TrendIcon
                              change={metric.trends.conversions_change}
                            />
                          </span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-600">Bounce Rate:</span>
                          <span className="flex items-center gap-1">
                            {metric.metrics.bounce_rate}%
                            <TrendIcon
                              change={-metric.trends.bounce_rate_change}
                            />
                          </span>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </TabsContent>

            <TabsContent value="top-performers">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Page</TableHead>
                    <TableHead>Type</TableHead>
                    <TableHead>Conversions</TableHead>
                    <TableHead>Conv. Rate</TableHead>
                    <TableHead>Page Views</TableHead>
                    <TableHead>Traffic Source</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {topPerformers.map((metric) => (
                    <TableRow key={metric.page_slug}>
                      <TableCell>
                        <div>
                          <div className="font-medium">{metric.page_name}</div>
                          <div className="text-sm text-gray-500">
                            {metric.parent_city}
                          </div>
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline">
                          {metric.page_type.replace("_", " ")}
                        </Badge>
                      </TableCell>
                      <TableCell className="font-bold">
                        {metric.metrics.conversions}
                      </TableCell>
                      <TableCell>{metric.metrics.conversion_rate}%</TableCell>
                      <TableCell>
                        {metric.metrics.page_views.toLocaleString()}
                      </TableCell>
                      <TableCell>{metric.metrics.top_traffic_source}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TabsContent>

            <TabsContent value="needs-attention">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Page</TableHead>
                    <TableHead>Issue</TableHead>
                    <TableHead>Conv. Rate</TableHead>
                    <TableHead>Bounce Rate</TableHead>
                    <TableHead>Page Views</TableHead>
                    <TableHead>Recommendation</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {needsAttention.map((metric) => (
                    <TableRow key={metric.page_slug}>
                      <TableCell>
                        <div>
                          <div className="font-medium">{metric.page_name}</div>
                          <div className="text-sm text-gray-500">
                            {metric.parent_city}
                          </div>
                        </div>
                      </TableCell>
                      <TableCell>
                        {metric.metrics.conversion_rate < 2 && (
                          <Badge variant="destructive">Low Conversion</Badge>
                        )}
                        {metric.metrics.bounce_rate > 70 && (
                          <Badge variant="destructive">High Bounce Rate</Badge>
                        )}
                      </TableCell>
                      <TableCell className="text-red-600">
                        {metric.metrics.conversion_rate}%
                      </TableCell>
                      <TableCell className="text-red-600">
                        {metric.metrics.bounce_rate}%
                      </TableCell>
                      <TableCell>
                        {metric.metrics.page_views.toLocaleString()}
                      </TableCell>
                      <TableCell className="text-sm">
                        {metric.metrics.conversion_rate < 2 && "Optimize CTAs"}
                        {metric.metrics.bounce_rate > 70 && "Improve content"}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TabsContent>

            <TabsContent value="detailed">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Page</TableHead>
                    <TableHead>Type</TableHead>
                    <TableHead>Page Views</TableHead>
                    <TableHead>Unique Visitors</TableHead>
                    <TableHead>Conversions</TableHead>
                    <TableHead>Conv. Rate</TableHead>
                    <TableHead>Bounce Rate</TableHead>
                    <TableHead>Avg. Duration</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredMetrics.map((metric) => (
                    <TableRow key={metric.page_slug}>
                      <TableCell>
                        <div>
                          <div className="font-medium">{metric.page_name}</div>
                          <div className="text-sm text-gray-500">
                            /{metric.page_slug}
                          </div>
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline">
                          {metric.page_type.replace("_", " ")}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        {metric.metrics.page_views.toLocaleString()}
                      </TableCell>
                      <TableCell>
                        {metric.metrics.unique_visitors.toLocaleString()}
                      </TableCell>
                      <TableCell className="font-bold">
                        {metric.metrics.conversions}
                      </TableCell>
                      <TableCell>{metric.metrics.conversion_rate}%</TableCell>
                      <TableCell>{metric.metrics.bounce_rate}%</TableCell>
                      <TableCell>
                        {formatDuration(metric.metrics.avg_session_duration)}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>
    </div>
  );
}
